import React, { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import PlacesAutocomplete, {
    geocodeByPlaceId,
    getLatLng
} from "react-places-autocomplete";
import firebase from "firebase";
import { useBars, useBarUtils, generateId } from "../actions/bars";
import { useUserRoles } from "../globalstore";
import { auth, firestore, geo } from "../services/firebase";
import supabase from "../services/supabaseClient";
import SignupCard from "../components/SignupCard";
import { captureError } from "../lib/errorReporting";

const ROLES = [
    { value: "owner", label: "Owner" },
    { value: "manager", label: "Manager" }
];

// Google Places autocomplete can only restrict to broad categories (e.g.
// "establishment"), not a specific "bar" type. We additionally prioritize
// bar-like venues using each prediction's place types, falling back to all
// establishment results when nothing bar-specific matches.
const BAR_PLACE_TYPES = ["bar", "night_club", "liquor_store", "restaurant"];

const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
    "DC"
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value) => EMAIL_REGEX.test((value || "").trim());

const countDigits = (value) => ((value || "").match(/\d/g) || []).length;

// Phone is optional, but when provided it must be a full 10-digit US number.
const isValidPhone = (value) => countDigits(value) === 10;

// Format a raw input into a US phone number, e.g. "(555) 123-4567".
// Caps at 10 digits so the value is never longer than a US number.
const formatPhoneNumber = (value) => {
    const digits = (value || "").replace(/\D/g, "").slice(0, 10);
    const len = digits.length;
    if (len === 0) {
        return "";
    }
    if (len < 4) {
        return `(${digits}`;
    }
    if (len < 7) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export function ClaimBar() {
    const { userRoles } = useUserRoles();
    const barsContext = useBars();
    const { initialFetch, setBarsQuery } = useBarUtils();

    const [searchTerm, setSearchTerm] = useState("");
    const [cantFind, setCantFind] = useState(false);
    const [selectedBar, setSelectedBar] = useState(null);
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [selectedRole, setSelectedRole] = useState("owner");
    const [placesQuery, setPlacesQuery] = useState("");
    const [googlePlace, setGooglePlace] = useState(null);
    const [googleNotFound, setGoogleNotFound] = useState(false);
    const [manualBarName, setManualBarName] = useState("");
    const [manualBarStreet, setManualBarStreet] = useState("");
    const [manualBarCity, setManualBarCity] = useState("");
    const [manualBarState, setManualBarState] = useState("");
    const [manualBarZip, setManualBarZip] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const { bars = [] } = barsContext || {};

    const currentUser = auth.currentUser;

    const hasRoles = Boolean(userRoles?.roles && Object.keys(userRoles.roles).length > 0);

    const emailError = contactEmail.trim().length > 0 && !isValidEmail(contactEmail);
    const phoneError = contactPhone.trim().length > 0 && !isValidPhone(contactPhone);

    useEffect(() => {
        if (!bars.length) {
            initialFetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentUser?.email) {
            setContactEmail((prev) => prev || currentUser.email);
        }
    }, [currentUser?.email]);

    const filteredBars = useMemo(() => {
        if (!Array.isArray(bars)) {
            return [];
        }

        const normalizedBars = bars.map((bar) => {
            if (typeof bar?.data === "function") {
                return { id: bar.id, ...bar.data() };
            }
            return bar;
        });

        if (!searchTerm.trim()) {
            return normalizedBars;
        }

        const term = searchTerm.trim().toLowerCase();
        return normalizedBars.filter((bar) => {
            const name = bar?.barName?.toLowerCase?.() || "";
            const address = bar?.barAddress?.toLowerCase?.() || "";
            return name.includes(term) || address.includes(term);
        });
    }, [bars, searchTerm]);

    const handlePlacesSelect = async (value, placeId) => {
        setError("");
        try {
            const results = await geocodeByPlaceId(placeId);
            const place = results[0];
            const latLng = await getLatLng(place);
            const rawName = place?.name || value;
            const primaryName = rawName?.split(",")[0]?.trim() || rawName;
            const address = place?.formatted_address || value;
            setGooglePlace({
                placeId,
                name: primaryName,
                address,
                lat: latLng?.lat ?? null,
                lng: latLng?.lng ?? null,
                lookupSource: "google"
            });
            setGoogleNotFound(false);
            setSelectedBar(null);
            setSuccess("");
        } catch (selectError) {
            captureError(selectError, { view: "ClaimBar", action: "selectPlace" });
            setError(selectError?.message || "Unable to select that place. Please try again.");
        }
    };

    const resetSelection = () => {
        setSelectedBar(null);
        setGooglePlace(null);
        setGoogleNotFound(false);
        setManualBarName("");
        setManualBarStreet("");
        setManualBarCity("");
        setManualBarState("");
        setManualBarZip("");
        setSuccess("");
        setError("");
    };

    const handleBarSelect = (entry) => {
        setSelectedBar({
            id: entry.id,
            barName: entry.barName,
            barAddress: entry.barAddress || "",
            lookupSource: "existing"
        });
        setGooglePlace(null);
        setGoogleNotFound(false);
        setSuccess("");
        setError("");
    };

    const handleSubmit = async () => {
        setError("");
        setSuccess("");

        if (!currentUser) {
            setError("Please sign in before claiming your bar.");
            return;
        }

        const googleSearchTerm = (placesQuery || searchTerm || "").trim();
        const isGoogleNotFoundFlow = cantFind && googleNotFound && !googlePlace;
        const activeSelection = cantFind ? googlePlace : selectedBar;

        if (!cantFind && !activeSelection) {
            setError("Select your bar before submitting.");
            return;
        }

        if (cantFind) {
            if (!googlePlace && !googleNotFound) {
                setError("Pick a Google result or confirm Google couldn't find your bar.");
                return;
            }
            if (googleNotFound && !manualBarName.trim()) {
                setError("Enter your bar's name so we can verify it.");
                return;
            }
            if (
                googleNotFound &&
                (!manualBarStreet.trim() ||
                    !manualBarCity.trim() ||
                    !manualBarState.trim())
            ) {
                setError("Enter your bar's street, city, and state so we can verify it.");
                return;
            }
        }

        if (!contactName.trim() || !contactEmail.trim()) {
            setError("Contact name and email are required.");
            return;
        }

        if (!isValidEmail(contactEmail)) {
            setError("Enter a valid email address.");
            return;
        }

        if (contactPhone.trim() && !isValidPhone(contactPhone)) {
            setError("Enter a 10-digit phone number, or leave it blank.");
            return;
        }

        if (!selectedRole) {
            setError("Select your role at the bar.");
            return;
        }

        setSubmitting(true);

        try {
            // Block duplicate claims: the same email cannot have more than one
            // open (pending) claim for the same bar. We match on the existing
            // bar id, the Google place id, or the bar name so duplicates are
            // caught across all three lookup flows.
            const normalizedEmail = contactEmail.trim().toLowerCase();
            const existingBarId = (!cantFind && activeSelection?.id) || null;
            const targetPlaceId = cantFind && googlePlace ? googlePlace.placeId : null;
            const targetName = (
                (cantFind
                    ? googlePlace?.name || manualBarName
                    : activeSelection?.barName || activeSelection?.name) || ""
            )
                .trim()
                .toLowerCase();

            // Non-admins can only read their own claims under Firestore rules, so
            // scope the duplicate lookup by submitter; admins can match by email.
            const isAdminUser =
                userRoles?.isAdmin === true || userRoles?.isAdmin === "true";
            const pendingQuery = isAdminUser
                ? firestore
                    .collection("claimed_bar_review")
                    .where("contactEmail", "==", normalizedEmail)
                : firestore
                    .collection("claimed_bar_review")
                    .where("userId", "==", currentUser.uid);

            const pendingSnap = await pendingQuery.get();

            const isDuplicate = pendingSnap.docs.some((doc) => {
                const d = doc.data();
                if (d.status !== "pending") {
                    return false;
                }
                if (existingBarId && d.barId === existingBarId) {
                    return true;
                }
                if (targetPlaceId && d.googlePlace?.placeId === targetPlaceId) {
                    return true;
                }
                return (
                    targetName &&
                    (d.barName || "").trim().toLowerCase() === targetName
                );
            });

            if (isDuplicate) {
                setError(
                    "You already have a pending claim for this bar. An admin will review it shortly."
                );
                return;
            }

            let barId = activeSelection?.id || null;
            let barName = (activeSelection?.barName || activeSelection?.name || "").trim();
            let barAddress = (activeSelection?.barAddress || activeSelection?.address || "").trim();
            let lookupSource =
                activeSelection?.lookupSource ||
                (isGoogleNotFoundFlow ? "google_not_found" : cantFind ? "google" : "existing");
            let googleData = null;

            if (lookupSource === "google") {
                const generatedId = generateId();
                const { lat, lng, name, address } = googlePlace;
                const normalizedName = name ? name.trim() : "";
                const normalizedAddress = address ? address.trim() : "";
                const payload = {
                    bar_id: generatedId,
                    bar_name: normalizedName,
                    bar_name_lower: normalizedName ? normalizedName.toLowerCase() : null,
                    bar_address: normalizedAddress,
                    bar_images: [],
                    bar_cover_image: null,
                    bar_type: null,
                    created_by: currentUser.uid,
                    owner_email: null,
                    geohash:
                        typeof lat === "number" && typeof lng === "number"
                            ? geo.point(Number(lat), Number(lng)).hash
                            : null,
                    location:
                        typeof lat === "number" && typeof lng === "number"
                            ? {
                                  type: "Point",
                                  coordinates: [Number(lng), Number(lat)]
                              }
                            : null,
                    data_source: "claim_google"
                };

                const { error: supabaseError } = await supabase
                    .from("bars")
                    .upsert(payload, { onConflict: "bar_id" });

                if (supabaseError) {
                    throw new Error(
                        supabaseError.message || "Unable to create the bar record right now."
                    );
                }

                const { error: reviewInsertError } = await supabase
                    .from("reviewed_and_contacted_bars")
                    .upsert(
                        {
                            bar_id: generatedId,
                            reviewed_by_admin: false,
                            contacted_by_agent: false
                        },
                        { onConflict: "bar_id", ignoreDuplicates: true }
                    );

                if (reviewInsertError) {
                    throw new Error(
                        reviewInsertError.message ||
                            "Unable to create the review tracking record right now."
                    );
                }

                barId = generatedId;
                barName = normalizedName;
                barAddress = normalizedAddress;
                googleData = {
                    placeId: googlePlace.placeId,
                    lat,
                    lng
                };
            }

            if (lookupSource === "google_not_found") {
                const enteredName = manualBarName.trim();
                const fallbackName = enteredName || googleSearchTerm || "Unlisted bar";
                const street = manualBarStreet.trim();
                const city = manualBarCity.trim();
                const state = manualBarState.trim();
                const zip = manualBarZip.trim();
                const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")]
                    .filter(Boolean)
                    .join(", ");
                barId = null;
                barName = fallbackName;
                barAddress = [street, cityStateZip].filter(Boolean).join(", ");
            }

            const claimData = {
                userId: currentUser.uid,
                userEmail: currentUser.email?.toLowerCase() || "",
                contactName: contactName.trim(),
                contactEmail: contactEmail.trim().toLowerCase(),
                contactPhone: contactPhone.trim(),
                role: selectedRole,
                barId,
                barName,
                barAddress,
                lookupSource,
                googleNotFound,
                status: "pending",
                createdAt: firebase.firestore.FieldValue.serverTimestamp()
            };

            if (lookupSource === "google") {
                claimData.googlePlace = {
                    ...googleData,
                    name: googlePlace.name,
                    address: googlePlace.address
                };
            }

            if (lookupSource === "google_not_found") {
                claimData.googleSearchTerm = googleSearchTerm || "";
                claimData.barStreet = manualBarStreet.trim();
                claimData.barCity = manualBarCity.trim();
                claimData.barState = manualBarState.trim();
                claimData.barZip = manualBarZip.trim();
                claimData.googlePlace = {
                    placeId: null,
                    name: manualBarName.trim() || googleSearchTerm || "Unlisted bar",
                    address: barAddress,
                    lat: null,
                    lng: null
                };
            }

            await firestore.collection("claimed_bar_review").add(claimData);

            setSuccess(
                "Thanks! An admin will reach out shortly to complete your bar verification."
            );
            setSelectedBar(null);
            setGooglePlace(null);
            setSearchTerm("");
            setPlacesQuery("");
            setContactPhone("");
            setCantFind(false);
            setGoogleNotFound(false);
            setManualBarName("");
            setManualBarStreet("");
            setManualBarCity("");
            setManualBarState("");
            setManualBarZip("");
        } catch (submitError) {
            captureError(submitError, { view: "ClaimBar", action: "submitClaim" });
            setError(
                submitError?.message || "Unable to submit your claim right now."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (!currentUser) {
        return (
            <SignupCard
                title="Claim Your Bar"
                description="Log in or sign up to claim your bar."
                onSuccess={() => window.location.reload()}
            />
        );
    }

    return (
        <Container>

            {hasRoles && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    You already have bar access. You can still submit another claim if needed.
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Section>
                <Typography variant="h6" gutterBottom>
                    Let&apos;s find your bar
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label="Search within Barhopper database"
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.target.value);
                            setBarsQuery(event.target.value);
                            resetSelection();
                        }}
                        fullWidth
                        disabled={cantFind}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={cantFind}
                                onChange={(event) => {
                                    setCantFind(event.target.checked);
                                    resetSelection();
                                }}
                            />
                        }
                        label="Try another way"
                    />
                    {!cantFind && (
                        <Typography variant="body2" color="textSecondary">
                            Select your bar from the list below.
                        </Typography>
                    )}
                </Box>

                {!cantFind && searchTerm.trim() && (
                    <BarList>
                        {filteredBars.map((entry) => {
                            const isSelected = selectedBar?.id === entry.id;
                            return (
                                <li key={entry.id}>
                                    <BarButton
                                        onClick={() => handleBarSelect(entry)}
                                        selected={isSelected}
                                    >
                                        <strong>{entry.barName}</strong>
                                        {entry.barAddress && (
                                            <span className="address">{entry.barAddress}</span>
                                        )}
                                    </BarButton>
                                </li>
                            );
                        })}
                        {filteredBars.length === 0 && (
                            <Typography variant="body2" color="textSecondary">
                                No bars matched that search.
                            </Typography>
                        )}
                    </BarList>
                )}

            {cantFind && (
                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        Search Google Maps
                    </Typography>
                    <PlacesAutocomplete
                        value={placesQuery}
                        onChange={(value) => {
                            setPlacesQuery(value);
                            setGooglePlace(null);
                            setGoogleNotFound(false);
                        }}
                        onSelect={handlePlacesSelect}
                        searchOptions={{ types: ["establishment"] }}
                    >
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
                            const barSuggestions = suggestions.filter((suggestion) =>
                                (suggestion.types || []).some((type) =>
                                    BAR_PLACE_TYPES.includes(type)
                                )
                            );
                            // Prefer bar-like venues, but fall back to all
                            // establishment results so a valid bar Google only
                            // tagged generically is never hidden.
                            const visibleSuggestions =
                                barSuggestions.length > 0 ? barSuggestions : suggestions;
                            return (
                            <div>
                                <TextField
                                    {...getInputProps({
                                        placeholder: "Start typing your bar name or address"
                                    })}
                                    fullWidth
                                    label="Google Places search"
                                />
                                <PlacesSuggestions>
                                    {loading && <div>Loading suggestions...</div>}
                                    {visibleSuggestions.map((suggestion) => {
                                        const className = suggestion.active ? "active" : "";
                                        return (
                                            <SuggestionOption
                                                key={suggestion.placeId}
                                                {...getSuggestionItemProps(suggestion, {
                                                    className
                                                })}
                                            >
                                                {suggestion.description}
                                            </SuggestionOption>
                                        );
                                    })}
                                    {!loading &&
                                        placesQuery.trim() &&
                                        visibleSuggestions.length === 0 && (
                                            <div>
                                                No bars matched that search. Try a different
                                                name, or check &quot;Google didn&#39;t find my
                                                bar&quot; below.
                                            </div>
                                        )}
                                </PlacesSuggestions>
                            </div>
                            );
                        }}
                    </PlacesAutocomplete>
                    {googlePlace && (
                        <SelectedPlace>
                            <strong>{googlePlace.name}</strong>
                            {googlePlace.address && (
                                <span className="address">{googlePlace.address}</span>
                            )}
                        </SelectedPlace>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={googleNotFound}
                                onChange={(event) => {
                                    setGoogleNotFound(event.target.checked);
                                    if (event.target.checked) {
                                        setGooglePlace(null);
                                    } else {
                                        setManualBarName("");
                                        setManualBarStreet("");
                                        setManualBarCity("");
                                        setManualBarState("");
                                        setManualBarZip("");
                                    }
                                }}
                            />
                        }
                        label="Google didn't find my bar"
                        sx={{ mt: 1 }}
                    />
                    {googleNotFound && (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <Typography variant="body2" color="textSecondary">
                                Tell us about your bar so an admin can verify and add it.
                            </Typography>
                            <TextField
                                label="Bar name"
                                value={manualBarName}
                                onChange={(event) => setManualBarName(event.target.value)}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Street address"
                                value={manualBarStreet}
                                onChange={(event) => setManualBarStreet(event.target.value)}
                                required
                                fullWidth
                            />
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <TextField
                                    label="City"
                                    value={manualBarCity}
                                    onChange={(event) => setManualBarCity(event.target.value)}
                                    required
                                    sx={{ flex: "2 1 160px" }}
                                />
                                <TextField
                                    select
                                    label="State"
                                    value={manualBarState}
                                    onChange={(event) => setManualBarState(event.target.value)}
                                    required
                                    sx={{ flex: "1 1 110px" }}
                                >
                                    {US_STATES.map((abbr) => (
                                        <MenuItem key={abbr} value={abbr}>
                                            {abbr}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="ZIP"
                                    value={manualBarZip}
                                    onChange={(event) => setManualBarZip(event.target.value)}
                                    inputProps={{ inputMode: "numeric", maxLength: 10 }}
                                    sx={{ flex: "1 1 110px" }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
            </Section>

            <Section>
                <Typography variant="h6" gutterBottom>
                    Your contact information
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label="Full name"
                        value={contactName}
                        onChange={(event) => setContactName(event.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Email"
                        type="email"
                        value={contactEmail}
                        onChange={(event) => setContactEmail(event.target.value)}
                        required
                        fullWidth
                        error={emailError}
                        helperText={emailError ? "Enter a valid email address." : ""}
                    />
                    <TextField
                        label="Phone"
                        type="tel"
                        value={contactPhone}
                        onChange={(event) =>
                            setContactPhone(formatPhoneNumber(event.target.value))
                        }
                        fullWidth
                        placeholder="(555) 123-4567"
                        inputProps={{ inputMode: "tel", maxLength: 14 }}
                        error={phoneError}
                        helperText={
                            phoneError
                                ? "Enter a 10-digit phone number, or leave it blank."
                                : ""
                        }
                    />
                    <Select
                        value={selectedRole}
                        onChange={(event) => setSelectedRole(event.target.value)}
                        fullWidth
                        displayEmpty
                    >
                        {ROLES.map((role) => (
                            <MenuItem key={role.value} value={role.value}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Section>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 2,
                    pt: 3,
                    pb: 5,
                    borderTop: 1,
                    borderColor: "divider"
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={
                        submitting ||
                        (!cantFind && !selectedBar) ||
                        (cantFind && !googlePlace && !googleNotFound) ||
                        !contactName.trim() ||
                        !contactEmail.trim() ||
                        emailError ||
                        phoneError ||
                        !selectedRole
                    }
                    endIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                    Submit for review
                </Button>
            </Box>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const Section = styled.section`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const suggestionCardStyles = css`
    border-radius: 12px;
    padding: 12px 16px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f8fafc;
    color: #0f172a;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
`;

const BarList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 12px;
`;

const BarButton = styled(Button).withConfig({
    shouldForwardProp: (prop) => prop !== "selected"
})`
    && {
        ${suggestionCardStyles};
        width: 100%;
        justify-content: flex-start;
        align-items: flex-start;
        text-transform: none;
        gap: 4px;
        background: ${({ selected }) =>
            selected ? "rgba(25, 118, 210, 0.16)" : "#f8fafc"};
        border-color: ${({ selected }) =>
            selected ? "rgba(25, 118, 210, 0.5)" : "rgba(15, 23, 42, 0.12)"};
        transition: background 0.2s ease, border-color 0.2s ease;
    }

    &&:hover {
        background: ${({ selected }) =>
            selected ? "rgba(25, 118, 210, 0.2)" : "#eef2ff"};
        border-color: rgba(25, 118, 210, 0.5);
    }

    strong {
        font-weight: 600;
    }

    .address {
        font-size: 0.85rem;
        color: rgba(15, 23, 42, 0.6);
    }
`;

const PlacesSuggestions = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SuggestionOption = styled.div`
    ${suggestionCardStyles};
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;

    background: #f8fafc;
    border-color: rgba(15, 23, 42, 0.12);

    &.active {
        background: rgba(25, 118, 210, 0.16);
        border-color: rgba(25, 118, 210, 0.5);
    }
`;

const SelectedPlace = styled.div`
    ${suggestionCardStyles};
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-color: rgba(25, 118, 210, 0.5);
    background: rgba(25, 118, 210, 0.16);
    text-transform: none;

    .label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: rgba(15, 23, 42, 0.6);
    }

    strong {
        font-weight: 600;
    }

    .address {
        font-size: 0.85rem;
        color: rgba(15, 23, 42, 0.6);
    }
`;

export default ClaimBar;
